import { useMemo } from 'react';
import { ScrollView, useWindowDimensions } from 'react-native';
import { TUTORIAL_SLIDE_VISUAL, type TutorialSlideVisual } from '../../constants/tutorial.constants';
import { getTutorialHeroArtLayoutStyles } from '../../src/tutorial/helpers/tutorialHeroArtLayout.helper';
import { CoreActionArt } from './CoreActionArt';
import { RewardArt } from './RewardArt';
import { FlexArt } from './FlexArt';
import { tutorialHeroArtStyles as styles } from './TutorialHeroArt.styles';

interface TutorialHeroArtProps {
  variant: TutorialSlideVisual;
  isActive: boolean;
  headline: string;
  subheadline?: string;
  testID?: string;
}

/**
 * Renders one of the three onboarding scenes (design flow 2a). Each scene owns its full
 * composition incl. headline placement; this resolves the responsive hero sizes, dispatches
 * on the slide variant (CORE_ACTION = Hook, REWARD = Duel, SOCIAL_FLEX = Climb), and hosts the
 * scene in a vertical ScrollView so short scenes stay centered while the taller Climb scene
 * scrolls instead of clipping on short viewports.
 */
export function TutorialHeroArt({ variant, isActive, headline, subheadline, testID }: TutorialHeroArtProps) {
  const { width } = useWindowDimensions();
  const layout = useMemo(() => getTutorialHeroArtLayoutStyles(width), [width]);
  const sceneProps = { isActive, layout, headline, subheadline };

  return (
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.content}
      showsVerticalScrollIndicator={false}
      testID={testID || 'tutorial-hero-art'}
    >
      {variant === TUTORIAL_SLIDE_VISUAL.CORE_ACTION && <CoreActionArt {...sceneProps} />}
      {variant === TUTORIAL_SLIDE_VISUAL.REWARD && <RewardArt {...sceneProps} />}
      {variant === TUTORIAL_SLIDE_VISUAL.SOCIAL_FLEX && <FlexArt {...sceneProps} />}
    </ScrollView>
  );
}
